import React, { useState, useEffect } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Grid,
  Stack,
  Tooltip,
  Typography,
  CircularProgress,
  Button,
} from "@mui/material";
import {
  formattedJson,
  getDateFromTimestamp,
  getFormattedTime,
  getTimeInHHMMSS,
} from "../../services/utils";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import { IconReRunWhite, IconWatchSvg } from "../../common/utils/icons";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import { downloadFileSecurely } from "../../services/fileDownloadService";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import ScoreBadge from "../../components/Scoring/ScoreBadge";
import ScoreDetailsDrawer from "../../components/Scoring/ScoreDetailsDrawer";
import { getGRQScoreHistory } from "../../services/scoringService";
import { useThemeMode } from "../../contexts/ThemeModeContext";
import { darkModeTokens } from "../../styles/Theme";
import { getExecutionSubGroupName } from "../../utils/executionGroup";
import useExecutionSubGroupMap from "../../hooks/useExecutionSubGroupMap";
const GenericRQDashboardDetails = ({ dashboardData }: any) => {
  console.log("dashboardData", dashboardData);
  const reqPath = dashboardData;
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const executionSubGroupMap = useExecutionSubGroupMap();
  const { mode } = useThemeMode();
  const isDark = mode === "dark";
  const canEditGenericResearchQuery = hasPermission(
    "research_query_execution_issues",
    "edit",
  );
  const canExecuteGenericResearchQuery = hasPermission(
    "generic_research_query",
    "execute",
  );
  const resourceNames = dashboardData?.execution_params?.resource_data || [];

  // State for tracking downloading files
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(
    new Set(),
  );

  // Generic RQ score state — lightweight header-only fetch via paginated
  // history (page=1, size=10 — backend minimum) and takes items[0]. Full
  // breakdown, history, and recalculation are handled inside the Drawer.
  const [isGRQScoreLoading, setIsGRQScoreLoading] = useState(false);
  const [grqScore, setGRQScore] = useState<any>(null);
  const [scoreDrawerOpen, setScoreDrawerOpen] = useState(false);

  useEffect(() => {
    const grqResultId = dashboardData?.id;
    if (!grqResultId || grqResultId <= 0) {
      setGRQScore(null);
      return;
    }
    let cancelled = false;
    setIsGRQScoreLoading(true);
    (async () => {
      try {
        const res = await getGRQScoreHistory({
          executionId: grqResultId,
          page: 1,
          pSize: 10,
        });
        if (!cancelled) setGRQScore(res?.items?.[0] || null);
      } catch {
        if (!cancelled) setGRQScore(null);
      } finally {
        if (!cancelled) setIsGRQScoreLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dashboardData?.id]);

  // Secure download handler
  const handleSecureDownload = async (filePath: string, fileName: string) => {
    if (!filePath || downloadingFiles.has(filePath)) return;

    setDownloadingFiles((prev) => new Set(prev).add(filePath));

    try {
      await downloadFileSecurely(filePath, fileName);
    } finally {
      setDownloadingFiles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(filePath);
        return newSet;
      });
    }
  };
  const formattedData = reqPath?.execution_params?.resource_data.map(
    (item: any, index: any) => {
      const {
        file_name,
        linked_service_code,
        connection_key,
        resource_path,
        resource_id,
        ...otherProps
      } = item || {};

      const resource_name = resourceNames.find(
        (rsItem: any) => rsItem?.resource_id === resource_id,
      )?.resource_name;
      return (
        <tr key={index}>
          <td>
            {resource_name} ({item?.resource_id})
          </td>
          <td>
            <div className="file-scroll">
              <div className="break-word w-240">
                <Box className="file-name-box">{item?.file_name ?? "N/A"}</Box>
              </div>
            </div>
          </td>
          <td>
            {item?.linked_service_code ? item?.linked_service_code : "N/A"}
          </td>
          <td>{item?.connection_key ? item?.connection_key : "N/A"}</td>
          <td>
            <div className="break-word w-240">
              {item.resource_path
                ? item.resource_path.replace(/"/g, "")
                : "N/A"}
            </div>
          </td>
          <td>
            <span className="position-relative">
              <Tooltip
                componentsProps={{
                  tooltip: { className: "wide-tooltip w-380" },
                }}
                title={
                  <pre
                    style={{
                      whiteSpace: "pre-wrap",
                      margin: 0,
                      maxHeight: "200px",
                      overflowY: "auto",
                    }}
                  >
                    <React.Fragment>
                      <Typography color="inherit">
                        Additional Resource Info
                      </Typography>
                      <Typography>
                        {formattedJson(JSON.stringify(otherProps))}
                      </Typography>
                    </React.Fragment>
                  </pre>
                }
              >
                <InfoIcon
                  sx={{
                    position: "absolute",
                    top: "50%",
                    transform: "translateY(-50%)",
                    right: "-24px",
                    width: "16px",
                    cursor: "pointer",
                  }}
                />
              </Tooltip>
            </span>
          </td>
        </tr>
      );
    },
  );

  const allVariables = reqPath?.execution_params?.inline_variables || {};
  const getReportPath = (path: string) => {
    if (path) {
      if (path.startsWith("\\")) {
        path = path.substring(1);
      }

      const startIndex = path.indexOf("download-files");

      let relativePath = path.substring(
        startIndex + "download-files".length + 1,
      );

      relativePath = relativePath.replace(/\\/g, "/");
      const parts = relativePath.split("/");
      const fileName = parts[parts.length - 1];
      const downloadLink = `${process.env.REACT_APP_REPORT_DOWNLOAD_PATH}${relativePath}`;
      return { downloadLink, fileName };
    }
  };

  return (
    <>
      <Box className="dashboard-title-group">
        <Stack
          direction="row"
          spacing={2.5}
          alignItems="center"
          flexWrap="wrap"
          divider={
            <Divider
              orientation="vertical"
              flexItem
              sx={{ mx: 0.5, borderColor: "divider" }}
            />
          }
          sx={{ width: "100%", rowGap: 1.5 }}
        >
          {/* CLUSTER 1 — Identity + Context */}
          <Box
            className="heading"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.25,
              flex: "1 1 auto",
              minWidth: 0,
            }}
          >
            <Tooltip
              title={reqPath?.execution_name || ""}
              placement="top"
              arrow
            >
              <Typography
                variant="h6"
                noWrap
                sx={{ fontWeight: 700, lineHeight: 1.3 }}
              >
                {reqPath?.execution_name}
              </Typography>
            </Tooltip>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              divider={
                <Box
                  component="span"
                  sx={{ color: "text.disabled", lineHeight: 1 }}
                >
                  ·
                </Box>
              }
              sx={{ color: "text.secondary", rowGap: 0.25 }}
            >
              {(dashboardData?.run_name || dashboardData?.run_id) && (
                <Typography variant="body2" color="inherit">
                  Execution Group: {dashboardData?.run_name ?? "—"}
                  {dashboardData?.run_id ? ` (${dashboardData?.run_id})` : ""}
                </Typography>
              )}
              {getExecutionSubGroupName(
                dashboardData,
                executionSubGroupMap,
              ) && (
                <Typography variant="body2" color="inherit">
                  Execution Sub Group:{" "}
                  {getExecutionSubGroupName(
                    dashboardData,
                    executionSubGroupMap,
                  )}
                </Typography>
              )}
              {dashboardData?.execution_time && (
                <Typography variant="body2" color="inherit">
                  {getDateFromTimestamp(dashboardData?.execution_time)}{" "}
                  {getFormattedTime(dashboardData?.execution_time)}
                </Typography>
              )}
              {dashboardData?.total_time != null && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    color: "inherit",
                  }}
                >
                  <IconWatchSvg />
                  <Typography variant="body2" color="inherit">
                    {getTimeInHHMMSS(dashboardData?.total_time)}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>

          {/* CLUSTER 3 — Result (Status + Score) */}
          {(dashboardData || isGRQScoreLoading || grqScore) && (
            <Stack direction="row" spacing={1} alignItems="center">
              {dashboardData && (
                <label
                  className={`badges badge-base ${
                    dashboardData?.job_status === "completed"
                      ? "badge-success"
                      : "badge-error"
                  }`}
                >
                  {dashboardData?.job_status === "completed"
                    ? "Completed"
                    : "Completed With Errors"}
                </label>
              )}
              {(isGRQScoreLoading || grqScore) &&
                (isGRQScoreLoading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={16} />
                  </Box>
                ) : (
                  <ScoreBadge
                    score={grqScore.score}
                    size="medium"
                    onClick={() => setScoreDrawerOpen(true)}
                    tooltipText="Click to view score details"
                  />
                ))}
            </Stack>
          )}

          {/* CLUSTER 4 — Actions (Re-run) */}
          {canExecuteGenericResearchQuery && (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button
                className="btn-orange btn-dark btn-sm plus-btn-sm"
                onClick={() =>
                  navigate(
                    `/research-execution-history/re-run-research-query-execution/${dashboardData?.generic_research_query_id}?re-run=true&execution-id=${dashboardData?.id}&run-id=${dashboardData?.run_id}&run-name=${dashboardData?.run_name}&execution-sub-group-id=${dashboardData?.execution_params?.execution_sub_group?.execution_sub_group_id}&execution-sub-group-code=${dashboardData?.execution_params?.execution_sub_group?.execution_sub_group_code}`,
                  )
                }
              >
                <IconReRunWhite /> &nbsp;ReRun
              </Button>
            </Stack>
          )}
        </Stack>
      </Box>
      <ScoreDetailsDrawer
        open={scoreDrawerOpen}
        onClose={() => setScoreDrawerOpen(false)}
        type="generic_rq"
        executionId={dashboardData?.id}
      />

      {reqPath && (
        <Box className="accordion-panel" sx={{ marginBottom: "8px" }}>
          <Accordion className="heading-bold">
            <AccordionSummary
              aria-controls="panel2d-content"
              id="panel2d-header"
              expandIcon={<GridExpandMoreIcon />}
            >
              <h4>Supporting Documents </h4>
            </AccordionSummary>
            <AccordionDetails sx={{ paddingTop: "16px" }}>
              <>
                {reqPath?.result_files?.research_query_result_files?.length >
                0 ? (
                  <table className="custom-table">
                    <tbody>
                      <tr>
                        <th style={{ width: "30%" }}>Name </th>
                        <th>Report Location </th>
                        <th>Resource Name </th>
                        <th>Filter Rules Result File </th>
                      </tr>

                      {reqPath?.result_files?.research_query_result_files.map(
                        (item: any) => {
                          if (item?.resource_result_files?.length === 0) {
                            return (
                              <tr key={item.research_query_name}>
                                <td>{item.research_query_name}</td>
                                <td>
                                  <Box className="word-break-all row-hr">
                                    <Box
                                      sx={{
                                        color: "text.primary",
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                      }}
                                      onClick={() => {
                                        const reportInfo = getReportPath(
                                          item?.result_file_path,
                                        );
                                        if (reportInfo?.fileName) {
                                          handleSecureDownload(
                                            item?.result_file_path,
                                            reportInfo.fileName,
                                          );
                                        }
                                      }}
                                    >
                                      {downloadingFiles.has(
                                        item?.result_file_path,
                                      ) && <CircularProgress size={16} />}
                                      {getReportPath(item?.result_file_path)
                                        ?.fileName ?? "N/A"}
                                    </Box>
                                  </Box>
                                </td>
                                <td colSpan={2} className="text-center">
                                  No resource result files
                                </td>
                              </tr>
                            );
                          }

                          return item?.resource_result_files?.map(
                            (file: any, index: number) => (
                              <tr
                                key={`${item.research_query_name}-${file.resource_name}`}
                              >
                                {index === 0 && (
                                  <>
                                    <td
                                      rowSpan={
                                        item.resource_result_files.length
                                      }
                                    >
                                      {item.research_query_name}
                                    </td>
                                    <td
                                      rowSpan={
                                        item.resource_result_files.length
                                      }
                                    >
                                      <Box className="word-break-all row-hr">
                                        <Box
                                          sx={{
                                            color: "text.primary",
                                            cursor: "pointer",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                          }}
                                          onClick={() => {
                                            const reportInfo = getReportPath(
                                              item?.result_file_path,
                                            );
                                            if (reportInfo?.fileName) {
                                              handleSecureDownload(
                                                item?.result_file_path,
                                                reportInfo.fileName,
                                              );
                                            }
                                          }}
                                        >
                                          {downloadingFiles.has(
                                            item?.result_file_path,
                                          ) && <CircularProgress size={16} />}
                                          {getReportPath(item?.result_file_path)
                                            ?.fileName ?? "N/A"}
                                        </Box>
                                      </Box>
                                    </td>
                                  </>
                                )}
                                <td>{file?.resource_name}</td>
                                <td>
                                  <Box
                                    sx={{
                                      color: "text.primary",
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                    onClick={() => {
                                      const reportInfo = getReportPath(
                                        file?.filter_rules_result_file,
                                      );
                                      if (reportInfo?.fileName) {
                                        handleSecureDownload(
                                          file?.filter_rules_result_file,
                                          reportInfo.fileName,
                                        );
                                      }
                                    }}
                                  >
                                    {downloadingFiles.has(
                                      file?.filter_rules_result_file,
                                    ) && <CircularProgress size={16} />}
                                    {getReportPath(
                                      file?.filter_rules_result_file,
                                    )?.fileName ?? "N/A"}
                                  </Box>
                                </td>
                              </tr>
                            ),
                          );
                        },
                      )}
                      <tr>
                        <td>Output Logs</td>
                        <td colSpan={3}>
                          {reqPath?.result_files?.output_logs
                            ? (() => {
                                const { fileName }: any = getReportPath(
                                  reqPath.result_files.output_logs,
                                );

                                return (
                                  <Box className="word-break-all row-hr">
                                    <Box
                                      sx={{
                                        color: "text.primary",
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                      }}
                                      onClick={() => {
                                        if (fileName) {
                                          handleSecureDownload(
                                            reqPath.result_files.output_logs,
                                            fileName,
                                          );
                                        }
                                      }}
                                    >
                                      {downloadingFiles.has(
                                        reqPath.result_files.output_logs,
                                      ) && <CircularProgress size={16} />}
                                      {fileName}
                                    </Box>
                                  </Box>
                                );
                              })()
                            : "N/A"}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      padding: "8px 24px",
                      fontSize: "15px",
                      fontWeight: "500",
                      color: "text.secondary",
                      border: `1px dashed ${isDark ? darkModeTokens.border : "#ccc"}`,
                      borderRadius: "8px",
                      backgroundColor: isDark
                        ? darkModeTokens.surfaceInset
                        : "#fafafa",
                    }}
                  >
                    No supporting documents available.
                  </Box>
                )}
              </>
            </AccordionDetails>
          </Accordion>
          <Accordion className="heading-bold">
            <AccordionSummary
              aria-controls="panel2d-content"
              id="panel2d-header"
              expandIcon={<GridExpandMoreIcon />}
            >
              <h4>Execution Parameters </h4>
            </AccordionSummary>
            <AccordionDetails sx={{ paddingTop: "16px" }}>
              {formattedData && formattedData.length > 0 ? (
                <table className="custom-table">
                  <tbody>
                    <tr>
                      <th>Resource Name (Resource ID)</th>
                      <th>File Name</th>
                      <th>Connection Type Code</th>
                      <th>Connection Key</th>
                      <th>Resource Location</th>
                      <th>Additional Resource Info</th>
                    </tr>
                    {formattedData}
                  </tbody>
                </table>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "8px 24px",
                    fontSize: "15px",
                    fontWeight: "500",
                    color: "text.secondary",
                    border: `1px dashed ${isDark ? darkModeTokens.border : "#ccc"}`,
                    borderRadius: "8px",
                    backgroundColor: isDark
                      ? darkModeTokens.surfaceInset
                      : "#fafafa",
                  }}
                >
                  No execution parameters available.
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        </Box>
      )}
      {dashboardData &&
        reqPath?.execution_params?.inline_variables &&
        Object.entries(reqPath?.execution_params?.inline_variables).length >
          0 && (
          <Box
            className="accordion-panel"
            sx={{
              marginBottom: "0px",
              marginTop: "8px",
            }}
          >
            <Accordion className="heading-bold color-palette color-palette-1">
              <AccordionSummary
                aria-controls="panel1d-content"
                id="panel1d-header"
                expandIcon={<GridExpandMoreIcon />}
              >
                Variables
              </AccordionSummary>
              <AccordionDetails sx={{ paddingTop: "16px" }}>
                <Grid container spacing={2.5}>
                  {Object.keys(allVariables).length > 0 && (
                    <Grid item xs>
                      <table className="inline-variables-table">
                        <tbody>
                          {Object.keys(allVariables).map(
                            (key: string, index: number) => {
                              return (
                                <tr key={index}>
                                  <td>
                                    <div className="inner-column">
                                      <div className="label">
                                        <strong>{key}</strong>
                                        <span className="required-asterisk">
                                          *
                                        </span>
                                        :
                                      </div>
                                      <input
                                        className="form-control-1 read-only"
                                        value={allVariables[key]}
                                        name={key}
                                        title={allVariables[key]}
                                        readOnly
                                      />
                                    </div>
                                  </td>
                                </tr>
                              );
                            },
                          )}
                        </tbody>
                      </table>
                    </Grid>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Box>
        )}
    </>
  );
};

export default GenericRQDashboardDetails;
